import {
  makeStyles,
  Typography,
  Button as MaterialButton,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import { getQuarter, subQuarters } from 'date-fns';
import { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { Role } from '../../api/user/Role';
import venturesAPI from '../../api/ventures';
import { VentureQuarterRecord } from '../../api/ventures/quarterlyrecords/types/VentureQuarterRecord';
import { VentureId } from '../../api/ventures/types/Venture';
import { PageLoader, Text } from '../../components/common';
import { ReportItem } from '../../components/ventures/report-item';
import { ReportLinkDialog } from '../../components/ventures/report-link-dialog';
import { UserContext } from '../../contexts/user-context';
import { getRoutePath, Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';

const useStyles = makeStyles({
  container: {
    maxWidth: 670,
  },
  report_link: {
    display: 'flex',
    alignItems: 'center',
    color: COLORS.COLOR_BLUE_BASE,
    cursor: 'pointer',
  },
  reports: {
    marginTop: 20,
  },
  report_form: {
    margin: '20px 0',
  },
  report_item: {
    '& + &': {
      marginTop: 10,
    },
  },
  centerBlock: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    flexGrow: 1,
  },
  linkButton: {
    whiteSpace: 'nowrap',
  },
  paragraph: {
    marginTop: 32,
  },
});

function VentureQuarterlyReportPage() {
  const classes = useStyles();
  const { ventureId } = useParams<{ ventureId: VentureId }>();
  const { hasRole } = useContext(UserContext);

  const [reports, setReports] = useState<VentureQuarterRecord[]>();
  const [openedReport, setOpenedReport] = useState<string>();
  const [isOpenReportLinkDialog, setIsOpenReportLinkDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loadReports = async (ventureId: string) => {
    try {
      setIsLoading(true);
      const loadedReports = await venturesAPI.getQuarterlyRecords(ventureId);
      setReports(loadedReports.filter((rep) => rep.status === 'SUBMITTED'));
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
    }
  };

  const handleOpenAccordion = (reportId: string) => () =>
    setOpenedReport(openedReport !== reportId ? reportId : undefined);

  useEffect(() => {
    loadReports(ventureId);
  }, [ventureId]);

  return (
    <>
      {isLoading ? (
        <div className={classes.centerBlock}>
          <PageLoader />
        </div>
      ) : !reports?.length && hasRole(Role.Founder) ? (
        <div className={classes.centerBlock}>
          <MaterialButton
            component={Link}
            to={getRoutePath(Pages.VENTURE_QUARTERLY_REPORT_FORM, {
              ventureId,
            })}
            variant='contained'
            color='primary'
            className={classes.linkButton}
            startIcon={<AddIcon />}
            data-testid='center-button-quarter-report'>
            Q{getQuarter(subQuarters(new Date(), 1))} Report
          </MaterialButton>
        </div>
      ) : (
        <div className={classes.container}>
          <Typography variant='h4'>Quarterly Reports</Typography>
          {!hasRole(Role.Founder) && (
            <div className={classes.paragraph}>
              <Text variant='normal'>
                Traction5 automatically sends this venture a link to submit its
                quarterly report at the end of each quarter. Submitted reports
                are available below. Each report has a link as well to manually
                request submission, or to submit corrected data.
              </Text>
              <br />
              <Text variant='normal'>
                Request the venture to submit{' '}
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    setIsOpenReportLinkDialog(true);
                  }}
                  href={'#'}>
                  its quarterly report for the previous quarter
                </a>
                .
              </Text>
            </div>
          )}
          <div className={classes.reports}>
            {reports?.map((report) => (
              <div className={classes.report_item} key={report.id}>
                <div
                  onClick={handleOpenAccordion(report.id)}
                  className={classes.report_link}>
                  {openedReport === report.id ? (
                    <ArrowDropUpIcon />
                  ) : (
                    <ArrowDropDownIcon />
                  )}
                  <Typography variant='h5'>
                    Q{report.quarterNumber} {report.year}
                  </Typography>
                </div>
                {openedReport === report.id && (
                  <div className={classes.report_form}>
                    <ReportItem report={report} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <ReportLinkDialog
        ventureIds={[ventureId]}
        isOpen={isOpenReportLinkDialog}
        onClose={() => setIsOpenReportLinkDialog(false)}
      />
    </>
  );
}

export default VentureQuarterlyReportPage;
